import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    COURT_DECISION_TYPE_OPTIONS,
    CourtDecisionType,
    type CourtDecision,
    type SelectOption,
    type UpdateCourtDecisionPayload,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

interface AddCourtDecisionValue {
    decisionType: CourtDecisionType;
    decisionDate: string;
    summary: string;
}

// Gerencia as decisões judiciais de um caso: lista, adiciona, edita campos e remove. Fetch
// próprio pelo id do caso (a seção atualiza sem recarregar o page-view inteiro). Mesmo padrão
// do useLegalCaseMeasures/useLegalCaseDeadlines. A decisão não tem ciclo de status — é fato
// congelado; a edição é parcial (updateDecision).
export default function useLegalCaseCourtDecisions(
    caseId: string | null,
    initialDecisions?: CourtDecision[],
) {
    const [decisions, setDecisions] = useState<CourtDecision[]>(initialDecisions || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [typeOptions, setTypeOptions] = useState<SelectOption[]>(COURT_DECISION_TYPE_OPTIONS);
    const seededCaseRef = useRef<string | null>(null);

    const fetchDecisions = useCallback(async () => {
        if (!caseId) return;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getCourtDecisions(caseId);
            setDecisions(result.items || []);
        } catch {
            // silencioso — a seção já mostra as decisões que vieram no page-view
        } finally {
            setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setDecisions(initialDecisions || []);
        setErrorFields([]);
        // initialDecisions fora das deps de propósito (referência nova a cada render).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        const loadTypes = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.courtDecisionTypes?.length) setTypeOptions(opts.courtDecisionTypes);
            } catch {
                // fallback local já preenche
            }
        };
        loadTypes();
    }, []);

    const clearErrors = () => setErrorFields([]);

    const addDecision = async (data: AddCourtDecisionValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addCourtDecision(caseId, {
                decisionType: data.decisionType,
                decisionDate: data.decisionDate,
                summary: data.summary || undefined,
            });
            await fetchDecisions();
            toast.success('Decisão registrada');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao registrar decisão');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const updateDecision = async (
        decisionId: string,
        data: UpdateCourtDecisionPayload,
    ): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        try {
            await recoveryApi.updateCourtDecision(caseId, decisionId, data);
            await fetchDecisions();
            toast.success('Decisão atualizada');
            return true;
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar decisão');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeDecision = async (decisionId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeCourtDecision(caseId, decisionId);
            await fetchDecisions();
            toast.success('Decisão removida');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover decisão');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        decisions, isLoading, isSaving, errorFields, typeOptions,
        addDecision, updateDecision, removeDecision, clearErrors,
    };
}

export type { AddCourtDecisionValue };
